import { ReportCatalogItem, ReportCategory } from './reportCatalog';

export interface FilterReportsOptions {
	searchTerm?: string;
	selectedCategories?: ReportCategory[];
	favoriteIds?: string[];
	favoritesOnly?: boolean;
	customOrder?: string[];
}

const normalizeText = (value: string | undefined): string => String(value ?? '').trim().toLowerCase();

const matchesSearchTerm = (report: ReportCatalogItem, searchTerm: string): boolean => {
	const normalizedSearchTerm = normalizeText(searchTerm);
	if (!normalizedSearchTerm) {
		return true;
	}

	return [
		report.name,
		report.description,
		report.platform,
		...report.categories,
		...report.kpis,
	]
		.map((entry) => normalizeText(entry))
		.some((entry) => entry.includes(normalizedSearchTerm));
};

export const applyCustomReportOrder = (
	reports: ReportCatalogItem[],
	customOrder: string[] | undefined
): ReportCatalogItem[] => {
	if (!customOrder || customOrder.length === 0) {
		return reports;
	}

	const reportMap = new Map(reports.map((report) => [report.id, report]));
	const orderedReports: ReportCatalogItem[] = [];

	customOrder.forEach((reportId) => {
		const report = reportMap.get(reportId);
		if (report) {
			orderedReports.push(report);
			reportMap.delete(reportId);
		}
	});

	return [...orderedReports, ...reportMap.values()];
};

export const filterReports = (
	reports: ReportCatalogItem[],
	{
		searchTerm,
		selectedCategories = [],
		favoriteIds = [],
		favoritesOnly = false,
		customOrder,
	}: FilterReportsOptions
): ReportCatalogItem[] => {
	const favoriteIdSet = new Set(favoriteIds);
	const orderedReports = applyCustomReportOrder(reports, customOrder);

	return orderedReports.filter((report) => {
		if (favoritesOnly && !favoriteIdSet.has(report.id)) {
			return false;
		}

		if (selectedCategories.length > 0 && !selectedCategories.some((category) => report.categories.includes(category))) {
			return false;
		}

		return matchesSearchTerm(report, searchTerm ?? '');
	});
};

export const buildCategoryCounts = (reports: ReportCatalogItem[]): Record<ReportCategory, number> => {
	return reports.reduce<Record<ReportCategory, number>>(
		(accumulator, report) => {
			report.categories.forEach((category) => {
				accumulator[category] += 1;
			});
			return accumulator;
		},
		{
			Safety: 0,
			People: 0,
			Accuracy: 0,
			Reliability: 0,
			Cost: 0,
			Navigator: 0,
		}
	);
};